import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Grid,
  Card,
  CardContent,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Tabs,
  Tab,
  Paper,
  IconButton,
  Tooltip,
  Fade,
  Zoom,
  Avatar,
} from '@mui/material';
import {
  TrendingUp,
  People,
  AttachMoney,
  CalendarToday,
  Refresh,
  Download,
  BarChart,
} from '@mui/icons-material';
import RevenueChart from '../components/dashboard/RevenueChart';
import { analyticsService } from '../services/analyticsService';
import { useAppDispatch } from '../hooks/redux';
import { addNotification } from '../store/slices/uiSlice';
import '../styles/minimal-padding.css';

// Enhanced Metric Card Component with forwardRef
interface MetricCardProps {
  title: string;
  value: string;
  change: number;
  changeType: 'increase' | 'decrease';
  bgGradient: string;
  icon: React.ReactNode;
}

const MetricCard = React.forwardRef<HTMLDivElement, MetricCardProps>(({
  title,
  value,
  change,
  changeType,
  bgGradient,
  icon,
}, ref) => (
  <Card
    ref={ref}
    className="metric-card"
    sx={{
      background: bgGradient,
      position: 'relative',
      overflow: 'hidden',
      minHeight: 140,
      borderRadius: 4,
      border: 'none',
      boxShadow: '0 8px 32px rgba(0, 0, 0, 0.15)',
      color: '#FFFFFF',
      cursor: 'pointer',
      transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
      '&:hover': {
        transform: 'translateY(-8px) scale(1.02)',
        boxShadow: '0 20px 40px rgba(0, 0, 0, 0.25)',
      },
      '&::before': {
        content: '""',
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        background: 'linear-gradient(45deg, transparent 30%, rgba(255,255,255,0.1) 50%, transparent 70%)',
        transform: 'translateX(-100%)',
        transition: 'transform 0.6s',
      },
      '&:hover::before': {
        transform: 'translateX(100%)',
      },
    }}
  >
    <CardContent sx={{ position: 'relative', zIndex: 2, p: 3, height: '100%' }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
        <Box sx={{ flex: 1 }}>
          <Typography 
            variant="caption" 
            sx={{ 
              color: '#FFFFFF !important',
              textTransform: 'uppercase',
              fontSize: '0.75rem',
              fontWeight: 700,
              letterSpacing: '1.5px',
              mb: 1,
              display: 'block',
              opacity: 0.9,
            }}
            style={{ color: '#FFFFFF !important' }}
          >
            {title}
          </Typography>
          <Typography 
            variant="h3" 
            sx={{ 
              color: '#FFFFFF !important',
              fontWeight: 800,
              fontSize: { xs: '1.8rem', sm: '2.2rem' },
              lineHeight: 1.2,
              mb: 1,
            }}
            style={{ color: '#FFFFFF !important' }}
          >
            {value}
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <TrendingUp sx={{ fontSize: 18, color: '#FFFFFF' }} />
            <Typography 
              variant="body2" 
              sx={{ 
                color: '#FFFFFF !important',
                fontSize: '0.85rem',
                fontWeight: 600,
                opacity: 0.9,
              }}
              style={{ color: '#FFFFFF !important' }}
            >
              {changeType === 'increase' ? '+' : '-'}{change}%
            </Typography>
          </Box>
        </Box>
        <Avatar 
          sx={{ 
            bgcolor: 'rgba(255, 255, 255, 0.2)', 
            width: 70, 
            height: 70,
            color: 'white',
            boxShadow: '0 8px 16px rgba(0, 0, 0, 0.2)',
          }}
        >
          <Box sx={{ fontSize: { xs: 30, sm: 35 } }}>
            {icon}
          </Box>
        </Avatar>
      </Box>
    </CardContent>
  </Card>
));

MetricCard.displayName = 'MetricCard';

const defaultChartData = {
  labels: [] as string[],
  datasets: [{ label: 'Revenue', data: [] as number[], borderColor: 'rgb(233, 30, 99)', backgroundColor: 'rgba(233, 30, 99, 0.1)', tension: 0.4 }],
};

const Analytics: React.FC = () => {
  const dispatch = useAppDispatch();
  const [tabValue, setTabValue] = useState(0);
  const [timeRange, setTimeRange] = useState('6months');
  const [loading, setLoading] = useState(true);
  const [analyticsData, setAnalyticsData] = useState({
    overview: { totalRevenue: 0, totalCustomers: 0, totalAppointments: 0, avgServiceValue: 0, revenueGrowth: 0, customerGrowth: 0, appointmentGrowth: 0 },
    revenueByMonth: defaultChartData,
    servicePopularity: { labels: [] as string[], datasets: [{ label: 'Bookings', data: [] as number[], backgroundColor: [] as string[] }] },
    customerSegments: { labels: [] as string[], datasets: [{ label: 'Customer Segments', data: [] as number[], backgroundColor: [] as string[] }] },
    staffPerformance: { labels: [] as string[], datasets: [] as { label: string; data: number[]; backgroundColor: string }[] },
  });

  const fetchAnalytics = async () => {
    setLoading(true);
    try {
      const [dashboard, revenue, trends] = await Promise.all([
        analyticsService.getDashboardStats(),
        analyticsService.getRevenueAnalytics(),
        analyticsService.getTrendData(180),
      ]);
      const monthlyRevenue = dashboard.monthlyStats?.revenue ?? revenue?.currentPeriod?.monthly ?? 0;
      const trendLabels = (trends || []).map((t: any) => t.date?.slice(0, 7) ?? '');
      const trendValues = (trends || []).map((t: any) => t.revenue ?? 0);
      setAnalyticsData((prev) => ({
        ...prev,
        overview: {
          totalRevenue: monthlyRevenue,
          totalCustomers: dashboard.monthlyStats?.newCustomers ?? 0,
          totalAppointments: dashboard.monthlyStats?.appointments ?? 0,
          avgServiceValue: revenue?.growth?.averageTransactionValue ?? 0,
          revenueGrowth: revenue?.metrics?.revenueGrowthRate ?? 0,
          customerGrowth: 0,
          appointmentGrowth: 0,
        },
        revenueByMonth: {
          labels: trendLabels.length ? trendLabels : ['No data'],
          datasets: [{ label: 'Revenue', data: trendValues.length ? trendValues : [0], borderColor: 'rgb(233, 30, 99)', backgroundColor: 'rgba(233, 30, 99, 0.1)', tension: 0.4 }],
        },
      }));
    } catch (err) {
      console.error('Failed to fetch analytics:', err);
      dispatch(addNotification({ type: 'error', message: err instanceof Error ? err.message : 'Failed to load analytics' }));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAnalytics();
  }, []);

  const handleTabChange = (_event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };

  const handleTimeRangeChange = (event: any) => {
    setTimeRange(event.target.value);
  };

  const mockAnalyticsData = analyticsData;

  return (
    <Box sx={{ width: '100%', height: '100%', p: 2 }}>
      {/* Enhanced Header Section with Animation */}
      <Paper 
        elevation={0} 
        sx={{ 
          p: 4, 
          mb: 3, 
          background: 'linear-gradient(135deg, #E8B4B8 0%, #F5D5D8 50%, #E8B4B8 100%)',
          borderRadius: 4,
          position: 'relative',
          overflow: 'hidden',
          boxShadow: '0 8px 32px rgba(232, 180, 184, 0.3)',
          '&::before': {
            content: '""',
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'linear-gradient(45deg, rgba(255,255,255,0.1) 0%, transparent 50%, rgba(255,255,255,0.1) 100%)',
            animation: 'shimmer 3s ease-in-out infinite',
          },
          '@keyframes shimmer': {
            '0%': { transform: 'translateX(-100%)' },
            '100%': { transform: 'translateX(100%)' },
          },
        }}
      >
        <Box sx={{ position: 'relative', zIndex: 2 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Box>
              <Typography 
                variant="h3" 
                component="h1" 
                sx={{ 
                  fontWeight: 800,
                  color: '#5C2A30',
                  mb: 1,
                }}
              >
                Business Analytics
              </Typography>
              <Typography 
                variant="h6" 
                sx={{ 
                  color: 'rgba(92, 42, 48, 0.85)',
                  fontSize: '1.2rem',
                  fontWeight: 500,
                }}
              >
                Comprehensive insights into your salon's performance
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', gap: 2 }}>
              <FormControl size="small" sx={{ minWidth: 140 }}>
                <InputLabel sx={{ color: 'rgba(92, 42, 48, 0.85)' }}>Time Range</InputLabel>
                <Select
                  value={timeRange}
                  label="Time Range"
                  onChange={handleTimeRangeChange}
                  sx={{
                    color: '#5C2A30',
                    bgcolor: 'rgba(255, 255, 255, 0.4)',
                    backdropFilter: 'blur(10px)',
                    border: '1px solid rgba(92, 42, 48, 0.18)',
                    borderRadius: 2,
                    '& .MuiOutlinedInput-notchedOutline': {
                      border: 'none',
                    },
                    '& .MuiSvgIcon-root': {
                      color: '#5C2A30',
                    },
                  }}
                >
                  <MenuItem value="1month">Last Month</MenuItem>
                  <MenuItem value="3months">Last 3 Months</MenuItem>
                  <MenuItem value="6months">Last 6 Months</MenuItem>
                  <MenuItem value="1year">Last Year</MenuItem>
                </Select>
              </FormControl>
              <Tooltip title="Refresh Data" arrow>
                <IconButton 
                  sx={{ 
                    bgcolor: 'rgba(255, 255, 255, 0.4)',
                    color: '#5C2A30',
                    backdropFilter: 'blur(10px)',
                    border: '1px solid rgba(92, 42, 48, 0.18)',
                    width: 48,
                    height: 48,
                    '&:hover': { 
                      bgcolor: 'rgba(255, 255, 255, 0.6)',
                      transform: 'translateY(-2px)',
                      boxShadow: '0 8px 25px rgba(0, 0, 0, 0.15)',
                    },
                    transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                  }}
                >
                  <Refresh />
                </IconButton>
              </Tooltip>
              <Tooltip title="Export Report" arrow>
                <IconButton 
                  sx={{ 
                    bgcolor: 'rgba(255, 255, 255, 0.4)',
                    color: '#5C2A30',
                    backdropFilter: 'blur(10px)',
                    border: '1px solid rgba(92, 42, 48, 0.18)',
                    width: 48,
                    height: 48,
                    '&:hover': { 
                      bgcolor: 'rgba(255, 255, 255, 0.6)',
                      transform: 'translateY(-2px)',
                      boxShadow: '0 8px 25px rgba(0, 0, 0, 0.15)',
                    },
                    transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                  }}
                >
                  <Download />
                </IconButton>
              </Tooltip>
            </Box>
          </Box>
        </Box>
        
        {/* Decorative Elements */}
        <Box
          sx={{
            position: 'absolute',
            top: -30,
            right: -30,
            width: 120,
            height: 120,
            borderRadius: '50%',
            background: 'radial-gradient(circle, rgba(255, 255, 255, 0.15) 0%, transparent 70%)',
            zIndex: 1,
          }}
        />
        <Box
          sx={{
            position: 'absolute',
            bottom: -20,
            left: -20,
            width: 80,
            height: 80,
            borderRadius: '50%',
            background: 'radial-gradient(circle, rgba(255, 255, 255, 0.1) 0%, transparent 70%)',
            zIndex: 1,
          }}
        />
      </Paper>

      {/* Enhanced KPI Overview */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={3}>
          <Zoom in timeout={300} style={{ transitionDelay: '100ms' }}>
            <MetricCard
              title="Total Revenue"
              value={`₹${(mockAnalyticsData.overview.totalRevenue / 1000).toFixed(0)}K`}
              change={mockAnalyticsData.overview.revenueGrowth}
              changeType="increase"
              bgGradient="linear-gradient(135deg, #10B981 0%, #059669 100%)"
              icon={<AttachMoney />}
            />
          </Zoom>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Zoom in timeout={400} style={{ transitionDelay: '200ms' }}>
            <MetricCard
              title="Total Customers"
              value={mockAnalyticsData.overview.totalCustomers.toString()}
              change={mockAnalyticsData.overview.customerGrowth}
              changeType="increase"
              bgGradient="linear-gradient(135deg, #8B5CF6 0%, #7C3AED 100%)"
              icon={<People />}
            />
          </Zoom>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Zoom in timeout={500} style={{ transitionDelay: '300ms' }}>
            <MetricCard
              title="Total Appointments"
              value={mockAnalyticsData.overview.totalAppointments.toString()}
              change={mockAnalyticsData.overview.appointmentGrowth}
              changeType="increase"
              bgGradient="linear-gradient(135deg, #06B6D4 0%, #0891B2 100%)"
              icon={<CalendarToday />}
            />
          </Zoom>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Zoom in timeout={600} style={{ transitionDelay: '400ms' }}>
            <MetricCard
              title="Avg Service Value"
              value={`₹${mockAnalyticsData.overview.avgServiceValue}`}
              change={5.8}
              changeType="increase"
              bgGradient="linear-gradient(135deg, #F97316 0%, #EA580C 100%)"
              icon={<BarChart />}
            />
          </Zoom>
        </Grid>
      </Grid>

      {/* Enhanced Analytics Tabs */}
      <Fade in timeout={700} style={{ transitionDelay: '500ms' }}>
        <Card 
          sx={{ 
            borderRadius: 4,
            background: 'rgba(255, 255, 255, 0.95)',
            backdropFilter: 'blur(20px)',
            border: '1px solid rgba(232, 180, 184, 0.2)',
            boxShadow: '0 12px 40px rgba(0, 0, 0, 0.1)',
            overflow: 'hidden',
            position: 'relative',
            '&::before': {
              content: '""',
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              height: '4px',
              background: 'linear-gradient(90deg, #E8B4B8 0%, #F5D5D8 50%, #E8B4B8 100%)',
            },
          }}
        >
          <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
            <Tabs 
              value={tabValue} 
              onChange={handleTabChange}
              sx={{
                '& .MuiTab-root': {
                  fontWeight: 600,
                  textTransform: 'none',
                  fontSize: '1rem',
                  minHeight: 64,
                  '&.Mui-selected': {
                    color: '#E8B4B8',
                  },
                },
                '& .MuiTabs-indicator': {
                  backgroundColor: '#E8B4B8',
                  height: 3,
                },
              }}
            >
              <Tab label="Revenue Analytics" />
              <Tab label="Service Analytics" />
              <Tab label="Customer Analytics" />
              <Tab label="Staff Performance" />
            </Tabs>
          </Box>

        <Box sx={{ p: 3 }}>
          {tabValue === 0 && (
            <Grid container spacing={3}>
              <Grid item xs={12}>
                <Card 
                  sx={{ 
                    borderRadius: 3,
                    background: 'rgba(255, 255, 255, 0.8)',
                    backdropFilter: 'blur(10px)',
                    border: '1px solid rgba(232, 180, 184, 0.1)',
                    boxShadow: '0 8px 25px rgba(0, 0, 0, 0.08)',
                  }}
                >
                  <CardContent sx={{ p: 3 }}>
                    <RevenueChart
                      title="Revenue Trend"
                      data={mockAnalyticsData.revenueByMonth}
                      type="line"
                      height={400}
                    />
                  </CardContent>
                </Card>
              </Grid>
              <Grid item xs={12} md={6}>
                <Card 
                  sx={{ 
                    borderRadius: 3,
                    background: 'rgba(255, 255, 255, 0.8)',
                    backdropFilter: 'blur(10px)',
                    border: '1px solid rgba(232, 180, 184, 0.1)',
                    boxShadow: '0 8px 25px rgba(0, 0, 0, 0.08)',
                  }}
                >
                  <CardContent sx={{ p: 3 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
                      <Avatar 
                        sx={{ 
                          bgcolor: '#E8B4B8', 
                          width: 48, 
                          height: 48,
                          boxShadow: '0 4px 12px rgba(232, 180, 184, 0.3)',
                        }}
                      >
                        <TrendingUp />
                      </Avatar>
                      <Typography variant="h6" sx={{ fontWeight: 700, color: 'text.primary' }}>
                        Revenue Insights
                      </Typography>
                    </Box>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', p: 2, bgcolor: 'rgba(232, 180, 184, 0.05)', borderRadius: 2 }}>
                        <Typography variant="body2" sx={{ fontWeight: 500 }}>Highest Revenue Month:</Typography>
                        <Typography variant="body2" sx={{ fontWeight: 700, color: '#E8B4B8' }}>June (₹1.25L)</Typography>
                      </Box>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', p: 2, bgcolor: 'rgba(232, 180, 184, 0.05)', borderRadius: 2 }}>
                        <Typography variant="body2" sx={{ fontWeight: 500 }}>Lowest Revenue Month:</Typography>
                        <Typography variant="body2" sx={{ fontWeight: 700, color: '#E8B4B8' }}>March (₹78K)</Typography>
                      </Box>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', p: 2, bgcolor: 'rgba(232, 180, 184, 0.05)', borderRadius: 2 }}>
                        <Typography variant="body2" sx={{ fontWeight: 500 }}>Average Monthly Revenue:</Typography>
                        <Typography variant="body2" sx={{ fontWeight: 700, color: '#E8B4B8' }}>₹1.01L</Typography>
                      </Box>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', p: 2, bgcolor: 'rgba(72, 187, 120, 0.05)', borderRadius: 2 }}>
                        <Typography variant="body2" sx={{ fontWeight: 500 }}>Growth Rate:</Typography>
                        <Typography variant="body2" sx={{ fontWeight: 700, color: '#48BB78' }}>
                          +15.2%
                        </Typography>
                      </Box>
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
              <Grid item xs={12} md={6}>
                <Card 
                  sx={{ 
                    borderRadius: 3,
                    background: 'rgba(255, 255, 255, 0.8)',
                    backdropFilter: 'blur(10px)',
                    border: '1px solid rgba(232, 180, 184, 0.1)',
                    boxShadow: '0 8px 25px rgba(0, 0, 0, 0.08)',
                  }}
                >
                  <CardContent sx={{ p: 3 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
                      <Avatar 
                        sx={{ 
                          bgcolor: '#4299E1', 
                          width: 48, 
                          height: 48,
                          boxShadow: '0 4px 12px rgba(66, 153, 225, 0.3)',
                        }}
                      >
                        <AttachMoney />
                      </Avatar>
                      <Typography variant="h6" sx={{ fontWeight: 700, color: 'text.primary' }}>
                        Revenue Breakdown
                      </Typography>
                    </Box>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', p: 2, bgcolor: 'rgba(66, 153, 225, 0.05)', borderRadius: 2 }}>
                        <Typography variant="body2" sx={{ fontWeight: 500 }}>Bridal Services:</Typography>
                        <Typography variant="body2" sx={{ fontWeight: 700, color: '#4299E1' }}>₹65K (52%)</Typography>
                      </Box>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', p: 2, bgcolor: 'rgba(139, 92, 246, 0.05)', borderRadius: 2 }}>
                        <Typography variant="body2" sx={{ fontWeight: 500 }}>Regular Services:</Typography>
                        <Typography variant="body2" sx={{ fontWeight: 700, color: '#8B5CF6' }}>₹35K (28%)</Typography>
                      </Box>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', p: 2, bgcolor: 'rgba(16, 185, 129, 0.05)', borderRadius: 2 }}>
                        <Typography variant="body2" sx={{ fontWeight: 500 }}>Packages:</Typography>
                        <Typography variant="body2" sx={{ fontWeight: 700, color: '#10B981' }}>₹20K (16%)</Typography>
                      </Box>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', p: 2, bgcolor: 'rgba(249, 115, 22, 0.05)', borderRadius: 2 }}>
                        <Typography variant="body2" sx={{ fontWeight: 500 }}>Products:</Typography>
                        <Typography variant="body2" sx={{ fontWeight: 700, color: '#F97316' }}>₹5K (4%)</Typography>
                      </Box>
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
            </Grid>
          )}

          {tabValue === 1 && (
            <Grid container spacing={3}>
              <Grid item xs={12} md={8}>
                <Card 
                  sx={{ 
                    borderRadius: 3,
                    background: 'rgba(255, 255, 255, 0.8)',
                    backdropFilter: 'blur(10px)',
                    border: '1px solid rgba(232, 180, 184, 0.1)',
                    boxShadow: '0 8px 25px rgba(0, 0, 0, 0.08)',
                  }}
                >
                  <CardContent sx={{ p: 3 }}>
                    <RevenueChart
                      title="Service Popularity"
                      data={mockAnalyticsData.servicePopularity}
                      type="bar"
                      height={400}
                    />
                  </CardContent>
                </Card>
              </Grid>
              <Grid item xs={12} md={4}>
                <Card 
                  sx={{ 
                    borderRadius: 3,
                    background: 'rgba(255, 255, 255, 0.8)',
                    backdropFilter: 'blur(10px)',
                    border: '1px solid rgba(232, 180, 184, 0.1)',
                    boxShadow: '0 8px 25px rgba(0, 0, 0, 0.08)',
                  }}
                >
                  <CardContent sx={{ p: 3 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
                      <Avatar 
                        sx={{ 
                          bgcolor: '#8B5CF6', 
                          width: 48, 
                          height: 48,
                          boxShadow: '0 4px 12px rgba(139, 92, 246, 0.3)',
                        }}
                      >
                        <BarChart />
                      </Avatar>
                      <Typography variant="h6" sx={{ fontWeight: 700, color: 'text.primary' }}>
                        Service Insights
                      </Typography>
                    </Box>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                      <Box sx={{ p: 3, bgcolor: 'rgba(232, 180, 184, 0.05)', borderRadius: 3 }}>
                        <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 600, mb: 1 }}>Most Popular Service</Typography>
                        <Typography variant="h6" sx={{ fontWeight: 700, color: '#E8B4B8', mb: 0.5 }}>Bridal Makeup</Typography>
                        <Typography variant="caption" sx={{ color: 'text.secondary' }}>85 bookings this month</Typography>
                      </Box>
                      <Box sx={{ p: 3, bgcolor: 'rgba(16, 185, 129, 0.05)', borderRadius: 3 }}>
                        <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 600, mb: 1 }}>Highest Revenue Service</Typography>
                        <Typography variant="h6" sx={{ fontWeight: 700, color: '#10B981', mb: 0.5 }}>Bridal Makeup</Typography>
                        <Typography variant="caption" sx={{ color: 'text.secondary' }}>₹4.25L revenue</Typography>
                      </Box>
                      <Box sx={{ p: 3, bgcolor: 'rgba(66, 153, 225, 0.05)', borderRadius: 3 }}>
                        <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 600, mb: 1 }}>Fastest Growing Service</Typography>
                        <Typography variant="h6" sx={{ fontWeight: 700, color: '#4299E1', mb: 0.5 }}>Facial Treatment</Typography>
                        <Typography variant="caption" sx={{ color: 'text.secondary' }}>+25% growth</Typography>
                      </Box>
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
            </Grid>
          )}

          {tabValue === 2 && (
            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                <Card 
                  sx={{ 
                    borderRadius: 3,
                    background: 'rgba(255, 255, 255, 0.8)',
                    backdropFilter: 'blur(10px)',
                    border: '1px solid rgba(232, 180, 184, 0.1)',
                    boxShadow: '0 8px 25px rgba(0, 0, 0, 0.08)',
                  }}
                >
                  <CardContent sx={{ p: 3 }}>
                    <RevenueChart
                      title="Customer Segments"
                      data={mockAnalyticsData.customerSegments}
                      type="bar"
                      height={400}
                    />
                  </CardContent>
                </Card>
              </Grid>
              <Grid item xs={12} md={6}>
                <Card 
                  sx={{ 
                    borderRadius: 3,
                    background: 'rgba(255, 255, 255, 0.8)',
                    backdropFilter: 'blur(10px)',
                    border: '1px solid rgba(232, 180, 184, 0.1)',
                    boxShadow: '0 8px 25px rgba(0, 0, 0, 0.08)',
                  }}
                >
                  <CardContent sx={{ p: 3 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
                      <Avatar 
                        sx={{ 
                          bgcolor: '#06B6D4', 
                          width: 48, 
                          height: 48,
                          boxShadow: '0 4px 12px rgba(6, 182, 212, 0.3)',
                        }}
                      >
                        <People />
                      </Avatar>
                      <Typography variant="h6" sx={{ fontWeight: 700, color: 'text.primary' }}>
                        Customer Analytics
                      </Typography>
                    </Box>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', p: 2, bgcolor: 'rgba(72, 187, 120, 0.05)', borderRadius: 2 }}>
                        <Typography variant="body2" sx={{ fontWeight: 500 }}>Customer Retention Rate:</Typography>
                        <Typography variant="body2" sx={{ fontWeight: 700, color: '#48BB78' }}>85%</Typography>
                      </Box>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', p: 2, bgcolor: 'rgba(232, 180, 184, 0.05)', borderRadius: 2 }}>
                        <Typography variant="body2" sx={{ fontWeight: 500 }}>Average Customer Lifetime Value:</Typography>
                        <Typography variant="body2" sx={{ fontWeight: 700, color: '#E8B4B8' }}>₹15,500</Typography>
                      </Box>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', p: 2, bgcolor: 'rgba(66, 153, 225, 0.05)', borderRadius: 2 }}>
                        <Typography variant="body2" sx={{ fontWeight: 500 }}>New Customer Acquisition:</Typography>
                        <Typography variant="body2" sx={{ fontWeight: 700, color: '#4299E1' }}>120 this month</Typography>
                      </Box>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', p: 2, bgcolor: 'rgba(249, 115, 22, 0.05)', borderRadius: 2 }}>
                        <Typography variant="body2" sx={{ fontWeight: 500 }}>Customer Satisfaction:</Typography>
                        <Typography variant="body2" sx={{ fontWeight: 700, color: '#F97316' }}>4.7/5 ⭐</Typography>
                      </Box>
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
            </Grid>
          )}

          {tabValue === 3 && (
            <Grid container spacing={3}>
              <Grid item xs={12}>
                <Card 
                  sx={{ 
                    borderRadius: 3,
                    background: 'rgba(255, 255, 255, 0.8)',
                    backdropFilter: 'blur(10px)',
                    border: '1px solid rgba(232, 180, 184, 0.1)',
                    boxShadow: '0 8px 25px rgba(0, 0, 0, 0.08)',
                  }}
                >
                  <CardContent sx={{ p: 3 }}>
                    <RevenueChart
                      title="Staff Performance Comparison"
                      data={mockAnalyticsData.staffPerformance}
                      type="bar"
                      height={400}
                    />
                  </CardContent>
                </Card>
              </Grid>
              <Grid item xs={12}>
                <Card 
                  sx={{ 
                    borderRadius: 3,
                    background: 'rgba(255, 255, 255, 0.8)',
                    backdropFilter: 'blur(10px)',
                    border: '1px solid rgba(232, 180, 184, 0.1)',
                    boxShadow: '0 8px 25px rgba(0, 0, 0, 0.08)',
                  }}
                >
                  <CardContent sx={{ p: 3 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
                      <Avatar 
                        sx={{ 
                          bgcolor: '#F97316', 
                          width: 48, 
                          height: 48,
                          boxShadow: '0 4px 12px rgba(249, 115, 22, 0.3)',
                        }}
                      >
                        <TrendingUp />
                      </Avatar>
                      <Typography variant="h6" sx={{ fontWeight: 700, color: 'text.primary' }}>
                        Staff Performance Summary
                      </Typography>
                    </Box>
                    <Grid container spacing={3}>
                      <Grid item xs={12} sm={6} md={3}>
                        <Box sx={{ textAlign: 'center', p: 3, bgcolor: 'rgba(232, 180, 184, 0.05)', borderRadius: 3 }}>
                          <Typography variant="h4" sx={{ color: '#E8B4B8', fontWeight: 800, mb: 1 }}>Preeti</Typography>
                          <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>Top Performer</Typography>
                          <Typography variant="caption" sx={{ color: 'text.secondary' }}>₹4.5L Revenue</Typography>
                        </Box>
                      </Grid>
                      <Grid item xs={12} sm={6} md={3}>
                        <Box sx={{ textAlign: 'center', p: 3, bgcolor: 'rgba(249, 115, 22, 0.05)', borderRadius: 3 }}>
                          <Typography variant="h4" sx={{ color: '#F97316', fontWeight: 800, mb: 1 }}>4.8⭐</Typography>
                          <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>Avg Rating</Typography>
                          <Typography variant="caption" sx={{ color: 'text.secondary' }}>Customer Satisfaction</Typography>
                        </Box>
                      </Grid>
                      <Grid item xs={12} sm={6} md={3}>
                        <Box sx={{ textAlign: 'center', p: 3, bgcolor: 'rgba(72, 187, 120, 0.05)', borderRadius: 3 }}>
                          <Typography variant="h4" sx={{ color: '#48BB78', fontWeight: 800, mb: 1 }}>440</Typography>
                          <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>Total Services</Typography>
                          <Typography variant="caption" sx={{ color: 'text.secondary' }}>This Period</Typography>
                        </Box>
                      </Grid>
                      <Grid item xs={12} sm={6} md={3}>
                        <Box sx={{ textAlign: 'center', p: 3, bgcolor: 'rgba(139, 92, 246, 0.05)', borderRadius: 3 }}>
                          <Typography variant="h4" sx={{ color: '#8B5CF6', fontWeight: 800, mb: 1 }}>₹2.8K</Typography>
                          <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>Avg Service Value</Typography>
                          <Typography variant="caption" sx={{ color: 'text.secondary' }}>Per Staff Member</Typography>
                        </Box>
                      </Grid>
                    </Grid>
                  </CardContent>
                </Card>
              </Grid>
            </Grid>
          )}
        </Box>
      </Card>
      </Fade>
    </Box>
  );
};

export default Analytics;